import type { HomeMetricsResponse, Period } from "@go-novus/admin-api";

type TrendPoint = HomeMetricsResponse["trend"][number];

export type TrendGrain = "day" | "week";

export interface TrendBucket {
  aiResolved: number;
  conversations: number;
  /** 桶覆盖的最后一天；逐日时与 `start` 同一天。 */
  end: string;
  start: string;
}

export interface BucketedTrend {
  buckets: TrendBucket[];
  grain: TrendGrain;
}

const DAYS_PER_WEEK = 7;
/** 一个桶连不成走势，凑不出两个就退回逐日——新店在近 30 天视图下常是这种。 */
const MIN_WEEK_BUCKETS = 2;

function toDailyBuckets(trend: readonly TrendPoint[]): TrendBucket[] {
  return trend.map((day) => ({
    aiResolved: day.aiResolved,
    conversations: day.conversations,
    end: day.date,
    start: day.date,
  }));
}

/**
 * 从最新一天往回切 7 天一桶，最旧的余数丢掉。
 *
 * 不要改成按自然周（周一到周日）切：30 天那样切会留下两个残周，最新那个天数不足、
 * 合计天然偏低，图上就是一段并不存在的下跌。往回切让每个桶各含一天周一到周日，
 * 周末的低谷在桶内自己抵消。余数也不要并进最旧那个桶，桶不等长就没法互相比。
 */
function toWeekBuckets(trend: readonly TrendPoint[]): TrendBucket[] {
  const dropped = trend.length % DAYS_PER_WEEK;
  const buckets: TrendBucket[] = [];

  for (const [index, day] of trend.slice(dropped).entries()) {
    const bucket = buckets[Math.floor(index / DAYS_PER_WEEK)];

    if (bucket === undefined) {
      buckets.push({
        aiResolved: day.aiResolved,
        conversations: day.conversations,
        end: day.date,
        start: day.date,
      });
      continue;
    }

    bucket.aiResolved += day.aiResolved;
    bucket.conversations += day.conversations;
    bucket.end = day.date;
  }

  return buckets;
}

/**
 * 把按日的趋势读模型收成图上要画的桶。近 30 天按周合计，逐日打点在三十个点上
 * 只剩锯齿（GON-42）；近 7 天维持逐日。
 */
export function bucketTrend(
  trend: HomeMetricsResponse["trend"],
  period: Period,
): BucketedTrend {
  if (period !== "last_30_days") {
    return { buckets: toDailyBuckets(trend), grain: "day" };
  }

  const weeks = toWeekBuckets(trend);

  return weeks.length < MIN_WEEK_BUCKETS
    ? { buckets: toDailyBuckets(trend), grain: "day" }
    : { buckets: weeks, grain: "week" };
}
